export const ROLES = { OWNER: "owner", PARTNER: "partner" } as const;
export type Role = (typeof ROLES)[keyof typeof ROLES];
export const ROLE_LABELS: Record<Role, string> = { owner: "录单端", partner: "代打端" };
export const ORDER_COLORS = ["黑色哑光PETG", "白色PETG"] as const;
export function isOrderColor(color: string): boolean {
  return ORDER_COLORS.some((allowed) => allowed === color);
}
export const FILE_EXTENSIONS = ["3mf", "stl", "step", "stp", "obj"] as const;
export const MAX_FILE_BYTES = 100 * 1024 * 1024;
export type OrderStatus = "pending" | "unpaid" | "paid";
export const STATUS_LABELS: Record<OrderStatus, string> = { pending: "待发货", unpaid: "待结算", paid: "已结清" };

export interface OrderItem {
  id: number;
  order_id: number;
  file_name: string;
  file_key: string;
  color: string;
  quantity: number;
  sort_order: number;
  downloaded_at: string | null;
}

export interface Order {
  id: number;
  order_time: string;
  xianyu_id: string | null;
  address: string;
  address_copied_at: string | null;
  notes: string | null;
  tracking_number: string | null;
  quote_cents: number | null;
  shipped_at: string | null;
  paid_at: string | null;
  created_at: string;
  items: OrderItem[];
}

export interface OwnerOrder extends Order {
  total_price_cents: number | null;
}
export type StoredOrder = OwnerOrder;

export function orderStatus(order: Pick<Order, "shipped_at" | "paid_at">): OrderStatus {
  return order.paid_at ? "paid" : order.shipped_at ? "unpaid" : "pending";
}

export function canConfirmReceipt(order: Pick<Order, "shipped_at" | "quote_cents" | "paid_at">): boolean {
  return Boolean(order.shipped_at) && order.quote_cents !== null && !order.paid_at;
}

export function orderForRole(order: StoredOrder, role: Role): Order | OwnerOrder {
  const { total_price_cents, ...publicOrder } = order;
  return role === ROLES.OWNER ? { ...publicOrder, total_price_cents } : publicOrder;
}

export function summarizeOrders(orders: readonly Order[]): { pending: number; unpaid: number; paid: number; payable_cents: number } {
  const summary = { pending: 0, unpaid: 0, paid: 0, payable_cents: 0 };
  for (const order of orders) {
    const status = orderStatus(order);
    summary[status] += 1;
    if (status === "unpaid") summary.payable_cents += order.quote_cents ?? 0;
  }
  return summary;
}

export function fileExtension(name: string): string | null {
  const extension = name.split(".").at(-1)?.toLowerCase();
  return FILE_EXTENSIONS.find((allowed) => allowed === extension) ?? null;
}

export function shippingDeadline(createdAt: string): string {
  const date = new Date(new Date(createdAt).getTime() + (8 + 3 * 24) * 60 * 60 * 1000);
  return date.toISOString().slice(0, 10);
}
